/* eslint-disable @typescript-eslint/no-confusing-void-expression */
import throttle from 'lodash.throttle';
import * as React from 'react';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import { transformSrtTracks } from 'srt-support-for-html5-videos';
import { IconToggleButton } from '@oneaudi/unified-web-components';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import type { Video, VideoPlayerProps, Videos } from '../../../types';
import { FA_LOCATOR_ID } from '../../../types';
import { Context } from '../../context/Context';
import { logVideoPlayerComponentTrackingData } from '../../utils/debugging';
import BackgroundImageComponent from '../background-image/background-image';
import { useIsFirstRender } from '../hooks/useIsFirstRender';
import { useTracking } from '../hooks/useTracking';
import { useSeekingStatus } from '../hooks/video/useSeekingStatus';
import { useVideo } from '../hooks/video/useVideo';
import type { BreakpointLabel } from '../../utils/breakpoints';
import { breakpoints } from '../../utils/breakpoints';
import { useVideoOverlayVisibility } from '../hooks/useAudioDescOverlayVisibility';
import { useI18n } from '../../i18n';
import { useBreakpoint } from '../hooks/useBreakpoint';

interface WrapperProps {
  isCover?: boolean;
  videos: Videos;
}

const Wrapper = styled.div<WrapperProps>`
  ${({ isCover }) =>
    isCover
      ? css`
          height: 100%;
        `
      : css`
          height: auto;
          object-fit: contain;
        `}
  position: relative;
  width: 100%;
`;

type StyledAdIconButtonProps = {
  showAdButton: boolean;
  isFullWidth?: boolean;
};
const StyledAdIconButton = styled(IconToggleButton)<StyledAdIconButtonProps>`
  position: absolute;
  top: 0rem;
  z-index: 5;
  right: 10%;
  padding: 12px;
  opacity: ${({ showAdButton }) => (showAdButton ? '1' : '0')};

  transform: translateY(0);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  pointer-events: auto;

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    bottom: ${({ isFullWidth = true }) => (isFullWidth ? '15%' : '25%')};
    right: 1%;
    top: auto;
  }
`;

interface VideoProps {
  isCover?: boolean;
  hideControls?: boolean;
}

const VideoComponent = styled.video<VideoProps>`
  height: 100%;
  ${({ isCover }): FlattenSimpleInterpolation =>
    !isCover &&
    css`
      top: 0;
    `};
  object-fit: ${({ isCover }): string => {
    // Ignore layout tests:
    /* istanbul ignore next */
    return isCover ? 'cover' : 'contain';
  }};
  width: 100%;
  ${({ hideControls }) =>
    hideControls &&
    css`
      pointer-events: none;
    `};

  &::-webkit-media-controls-timeline {
    display: block;
  }
  // styling the subtitle
  &::cue {
    background: rgba(0, 0, 0, 0.6);
    font-size: 20px;
    font-family: AudiType, sans-serif;
    text-shadow: 1px 1px 2px black;
    opacity: 0.9;
  }
`;

/*
 * the hash at the end of the URL fixes a bug on Safari when the first
 * frame is not used as poster image when no custom poster image is defined
 */
const fixSrc = (poster: boolean, src: string) => {
  return poster ? src : `${src}#t=0.001`;
};

export const VideoPlayer: FC<VideoPlayerProps> = ({ videos, cover, playsInline, isFormatable }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const overlayRef = useRef(null);

  const isFirstRender = useIsFirstRender();
  const uniqueFaName = __FEATURE_APP_NAME__ + React.useId();

  const { videoService, localeService } = useContext(Context);
  const { breakpoint: currentBreakpoint, nextAvailableBreakPoint } = useBreakpoint(videos);
  const currentVideo = videos[currentBreakpoint] ?? videos[nextAvailableBreakPoint];

  /* In order to autoplay videos on modern browsers the should be initially muted */
  const [showReplayButton, setShowReplayButton] = useState(false);
  const [audioDescriptionEnabled, setAudioDescriptionEnabled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(videos.xs.autoPlay);
  const [isAtStart, setIsAtStart] = useState(true);
  const [wasStartedInitially, setWasStartedInitially] = useState(false);
  const overlayVisible = useVideoOverlayVisibility(videoRef.current, overlayRef);

  const isSeeking = useSeekingStatus(videoRef);
  const { videoProps } = useVideo(videoRef, currentVideo);
  const enableAudioDescriptionAriaLabel = useI18n('enableAudioDescriptionAriaLabel');
  const disableAudioDescriptionAriaLabel = useI18n('disableAudioDescriptionAriaLabel');

  const onEnded = useCallback(() => {
    setShowReplayButton(true);
    setIsPlaying(false);
  }, []);

  const onPlay = useCallback(() => {
    if (!wasStartedInitially) {
      setWasStartedInitially(true);
    }
    setShowReplayButton(false);
    setIsPlaying(true);
  }, []);

  const hasPosterImage = useMemo<boolean>(() => {
    return videos.xs.poster !== undefined;
  }, [videos]);

  useEffect(() => {
    videoService?.registerVideoRef(videoRef);
    return (): void => {
      videoService?.unregisterVideoRef();
    };
  }, [videoService]);

  useEffect(() => {
    const videoElement = videoRef.current;

    const updateTime = throttle((): void => {
      setIsAtStart(() => {
        return videoElement.currentTime <= 0.001;
      });

      tracking.videoPercentagePlayedEvent(videoElement.currentTime, videoElement.duration);
    }, 250);

    const transformSRT = async () => {
      await transformSrtTracks(videoElement);
    };

    const setIsNotPlaying = (): void => {
      setIsPlaying(false);
    };

    videoElement?.addEventListener('timeupdate', updateTime);
    videoElement?.addEventListener('pause', setIsNotPlaying);

    transformSRT().catch(() => {});

    return (): void => {
      videoElement?.removeEventListener('timeupdate', updateTime);
      videoElement?.removeEventListener('pause', setIsNotPlaying);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video || !audio) return undefined;

    const syncTime = () => (audio.currentTime = video.currentTime);
    const syncRate = () => (audio.playbackRate = video.playbackRate);

    // Prevent trying to play audio beyond its length
    const pauseAudioIfVideoLonger = () => {
      if (video.currentTime >= audio.duration) {
        if (!audio.paused) audio.pause();
        return true; // indicates "audio was stopped"
      }
      return false;
    };
    const syncAudio = () => {
      // If video has looped and audio has ended, reset audio to beginning
      if (video.currentTime < 0.5 && audio.ended) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }

      if (pauseAudioIfVideoLonger()) return;
    };

    const playAudio = () => {
      syncTime();
      syncRate();
      if (pauseAudioIfVideoLonger()) return;

      audio.play().catch(() => {});
    };

    const stopAudio = () => {
      audio.pause();
      audio.currentTime = 0;
    };

    // apply initial state
    if (!audioDescriptionEnabled) {
      stopAudio();
    } else if (!video.paused) {
      playAudio();
    }

    const handlePause = () => audio.pause();
    const handleSeeked = () => audioDescriptionEnabled && syncTime();
    const handleEnded = () => stopAudio();
    const handlePlay = () => {
      audioDescriptionEnabled && playAudio();
    };

    // bind events
    video.addEventListener('ratechange', syncRate);
    video.addEventListener('timeupdate', syncAudio);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('ratechange', syncRate);
      video.addEventListener('timeupdate', syncAudio);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('ended', handleEnded);
    };
  }, [audioDescriptionEnabled]);

  useEffect(() => {
    if (isAtStart && !isPlaying) {
      setShowReplayButton(false);
    }
  }, [isAtStart, isPlaying]);

  // Setup tracking START
  const tracking = useTracking(videoRef, {
    implementer: 37,
  });

  logVideoPlayerComponentTrackingData('tracking init started');

  useEffect(() => {
    if (!isFirstRender) {
      tracking.videoStartEvent();
    }
  }, [wasStartedInitially]);

  useEffect(() => {
    if (!isFirstRender) {
      if (isAtStart) {
        // skip this playing status change because of user has started the video
        return;
      }
      if (videoRef.current.currentTime >= videoRef.current.duration) {
        // skip this playing status change because video reached the end
        return;
      }
      if (videoRef.current.readyState === 1 || videoRef.current.seeking || isSeeking) {
        // skip this playing status change because video is seeking
        return;
      }

      // playing status must have been toggled by the user
      if (isPlaying) {
        tracking.videoResumeEvent();
      } else {
        tracking.videoPauseEvent();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    tracking.sendReadyEvent();
    tracking.registerImpressionTracking();

    logVideoPlayerComponentTrackingData('tracking init finished');
  }, []);
  // Setup tracking END

  const audioDescriptionLabel = audioDescriptionEnabled
    ? disableAudioDescriptionAriaLabel
    : enableAudioDescriptionAriaLabel;

  const currentHasPoster = !!currentVideo.poster;

  return (
    <Wrapper
      isCover={cover}
      videos={videos}
      data-test-id={FA_LOCATOR_ID}
      role="group"
      aria-label={videos.xs.nativeControls && videos.xs.alt}
    >
      <VideoComponent
        autoPlay={currentVideo.autoPlay}
        isCover={cover}
        key={videos.xs.src}
        loop={videos.xs.loop}
        crossOrigin="anonymous"
        // In order to autoplay videos on modern browsers the should be initially muted
        muted={currentVideo.autoPlay || currentVideo.muted}
        controls={currentVideo.nativeControls && !currentVideo.hideControls}
        onEnded={onEnded}
        onPlay={onPlay}
        playsInline={playsInline}
        poster={currentVideo.nativeControls && hasPosterImage ? currentVideo.poster : ''}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        preload="metadata"
        // @ts-expect-error ref is fine
        ref={videoRef}
        {...videoProps}
      >
        {Object.entries(videos)
          .map(
            ([breakpoint, video]: [breakpoint: BreakpointLabel, video: Video]): [number, Video] => [
              breakpoints[breakpoint],
              video,
            ],
          )
          .sort(([a], [b]) => b - a)
          .map(([minWidth, { src }]) => (
            <source
              media={minWidth > 0 ? `(min-width:${minWidth}px)` : undefined}
              src={fixSrc(currentHasPoster, src)}
              key={src}
            />
          ))}
        {currentVideo.caption && (
          <track
            label={localeService.language.toUpperCase()}
            kind="captions"
            srcLang={localeService.language}
            src={currentVideo.caption}
          />
        )}
      </VideoComponent>
      {currentVideo.audioDescription && !currentVideo.hideControls && (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            ref={audioRef}
            src={currentVideo.audioDescription}
            hidden
            aria-hidden={!audioDescriptionEnabled}
            aria-label="Audio description track for the video"
            preload="auto"
          />
          <StyledAdIconButton
            ref={overlayRef}
            showAdButton={overlayVisible}
            isFullWidth={currentVideo.isFullWidth}
            id={uniqueFaName}
            visuallyHideLabel
            label={audioDescriptionLabel}
            aria-label={audioDescriptionLabel}
            size="sm"
            value=""
            variant={process.env.IS_LEGACY ? 'primary' : 'secondary'}
            checked={audioDescriptionEnabled}
            icon={audioDescriptionEnabled ? 'audio-description-active' : 'audio-description'}
            onChange={() => {
              setAudioDescriptionEnabled(!audioDescriptionEnabled);
            }}
          />
        </>
      )}

      {!currentVideo.nativeControls &&
        !currentVideo.hideControls &&
        currentVideo.poster &&
        !isPlaying &&
        !showReplayButton &&
        isAtStart && <BackgroundImageComponent {...{ isFormatable }} videos={videos} />}
    </Wrapper>
  );
};
